import { useEffect, useRef } from 'react'
import { useAppStore } from '@/store/useAppStore.ts'
import { Settings, X, Trash2, Info } from 'lucide-react'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onOpenAbout: () => void
}

export function SettingsModal({ isOpen, onClose, onOpenAbout }: SettingsModalProps) {
  const clearDemoData = useAppStore((s) => s.clearDemoData)
  const showLoadDemo = useAppStore((s) => s.showLoadDemo)
  const setShowLoadDemo = useAppStore((s) => s.setShowLoadDemo)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isOpen) {
      closeRef.current?.focus()
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
      }
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="demo-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="demo-modal">
        <div className="demo-modal__header">
          <h2 className="demo-modal__title">
            <Settings size={20} />
            Settings
          </h2>
          <button
            ref={closeRef}
            onClick={onClose}
            className="demo-modal__close"
            aria-label="Close"
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        <div className="settings-modal__body">
          <div className="settings-modal__section">
            <h3 className="settings-modal__section-title">Demo Data</h3>
            <div className="settings-modal__row">
              <div className="settings-modal__label-group">
                <span className="settings-modal__label">Show Load Demo option</span>
                <span className="settings-modal__hint">Display the "Load Demo" button in the header</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showLoadDemo}
                className={`settings-modal__toggle ${showLoadDemo ? 'settings-modal__toggle--on' : ''}`}
                onClick={() => setShowLoadDemo(!showLoadDemo)}
              >
                <span className="settings-modal__toggle-knob" />
              </button>
            </div>
            <div className="settings-modal__row">
              <div className="settings-modal__label-group">
                <span className="settings-modal__label">Clear demo data</span>
                <span className="settings-modal__hint">Reset all inputs to defaults</span>
              </div>
              <button
                type="button"
                className="settings-modal__danger-btn"
                onClick={() => {
                  clearDemoData()
                  onClose()
                }}
              >
                <Trash2 size={14} />
                Clear
              </button>
            </div>
          </div>
          <hr className="settings-modal__divider" />
          <div className="settings-modal__section">
            <button
              type="button"
              className="settings-modal__about-btn"
              onClick={() => {
                onClose()
                onOpenAbout()
              }}
            >
              <Info size={16} />
              About
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
